import { useSelector, useDispatch } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { palette } from '@m12s/component-library';
import _capitalize from 'lodash/capitalize';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import styled from 'styled-components';
import { farSyncAlt } from 'lib/icons';

import { SearchInput } from 'components/SearchInput';

import { actionPatchSelectWorkOrderOpOptions } from 'lib/actions';
import { getProductionScheduleEnabled } from 'lib/selectors/getProductionScheduleEnabled';
import SwitchToggle from 'components/SwitchToggle';
import { Text } from 'components/Text';
import { getSelectWorkOrderOpOptions } from 'lib/selectors/getSelectWorkOrderOp';
import { getMachineSchedule } from 'lib/selectors/getMachineSchedule';

import { SpinningIcon } from './styled';
import useRequestUnscheduledWorkOrderOps from './hooks/useRequestUnscheduledWorkOrderOps';

const SwitchToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
`;

const SwitchToggleText = styled(Text)`
  font-size: 0.875rem;
  color: ${palette.Grey700};
  font-weight: 500;
`;

const useSelectOperationFlow = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const queryParams = queryString.parse(history.location.search);
  const productionScheduleEnabled = useSelector(getProductionScheduleEnabled);

  const { onlyMachineChecked, isSearchMode, search } = useSelector(
    getSelectWorkOrderOpOptions
  );

  const [
    requestMachineWorkOrderOps,
    {
      operations: machineScheduledWorkOrderOps,
      loading: requestMachineWorkOrderOpsLoading,
      error: requestMachineWorkOrderOpsError,
    },
  ] = useRequestUnscheduledWorkOrderOps({
    onlyAssignedToThisMachine: true,
    skip: onlyMachineChecked && !isSearchMode,
  });

  const [
    requestNotMachineWorkOrderOps,
    {
      operations: notMachineScheduledWorkOrderOps,
      loading: requestNotMachineWorkOrderOpsLoading,
      error: requestNotMachineWorkOrderOpsError,
    },
  ] = useRequestUnscheduledWorkOrderOps({
    onlyNotAssignedToThisMachine: true,
    skip: onlyMachineChecked || !isSearchMode,
  });

  const requestWorkOrderOps = useCallback(
    (opts) => {
      requestMachineWorkOrderOps(opts);
      requestNotMachineWorkOrderOps(opts);
    },
    [requestMachineWorkOrderOps, requestNotMachineWorkOrderOps]
  );

  const setOnlyMachineChecked = useCallback(
    (value) => {
      dispatch(
        actionPatchSelectWorkOrderOpOptions({
          onlyMachineChecked: value,
        })
      );
    },
    [dispatch]
  );

  const setSearch = useCallback(
    (value) => {
      dispatch(
        actionPatchSelectWorkOrderOpOptions({
          search: value,
        })
      );
    },
    [dispatch]
  );

  const [machineScheduleInit, setMachineScheduleInit] = useState(false);
  const machineSchedule = useSelector(getMachineSchedule);

  const excludeOperations = useMemo(() => {
    return queryParams?.excludeWorkOrderOperationRefs
      ? queryParams.excludeWorkOrderOperationRefs.split(',').map((ref) => {
          return ref;
        })
      : [];
  }, [queryParams.excludeWorkOrderOperationRefs]);

  useEffect(() => {
    if (!productionScheduleEnabled) {
      requestWorkOrderOps();
    } else if (machineSchedule?.length === 0) {
      if (!machineScheduleInit) {
        setMachineScheduleInit(true);
        setOnlyMachineChecked(false);
      }
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [productionScheduleEnabled, machineSchedule]);

  useEffect(() => {}, [onlyMachineChecked, isSearchMode]);

  useEffect(() => {
    if (isSearchMode && search?.length >= 0) {
      debouncedRequestMachineWorkOrderOps();
      debouncedRequestNotMachineWorkOrderOps();
    }
  }, [search]);

  const debouncedRequestMachineWorkOrderOps = debounce(
    requestMachineWorkOrderOps,
    850
  );
  const debouncedRequestNotMachineWorkOrderOps = debounce(
    requestNotMachineWorkOrderOps,
    850
  );

  const handleCheck = useCallback(() => {
    const newCheckedState = !onlyMachineChecked;
    setOnlyMachineChecked(newCheckedState);
  }, [onlyMachineChecked, setOnlyMachineChecked]);

  const pageLoading =
    requestMachineWorkOrderOpsLoading || requestNotMachineWorkOrderOpsLoading;
  const error =
    requestMachineWorkOrderOpsError || requestNotMachineWorkOrderOpsError;

  const Components = {
    SearchInput: (
      <SearchInput
        loading={pageLoading}
        iconPosition="start"
        inputStyle={{
          fontSize: '0.875rem',
          fontWeight: 400,
          color: palette.Grey600,
          height: '2.5rem',
        }}
        inputProps={{
          value: search,
          onChange: ({ target: { value } }) => {
            setSearch(value);
          },
          placeholder: _capitalize(
            t('Search by order, part, operation, or resource')
          ),
        }}
      />
    ),
    SwitchToggle: pageLoading ? (
      <SpinningIcon icon={farSyncAlt} style={{ marginRight: '.5rem' }} />
    ) : (
      <SwitchToggleContainer>
        <SwitchToggle
          onChange={handleCheck}
          isChecked={onlyMachineChecked}
          disabled={pageLoading}
        />
        <SwitchToggleText>
          {t(isSearchMode ? 'Assigned jobs only' : 'Scheduled jobs only')}
        </SwitchToggleText>
      </SwitchToggleContainer>
    ),
  };

  return {
    pageLoading,
    excludeOperations,
    machineScheduledWorkOrderOps,
    notMachineScheduledWorkOrderOps,
    error,
    Components,
  };
};

useSelectOperationFlow.propTypes = {
  setManualModalOpen: PropTypes.func,
  setNewRunModalOpen: PropTypes.func,
};

export default useSelectOperationFlow;
